import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';

import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';
import { UserProfile } from '../../core';
import { addDays, eventImage, userImage } from '../../core/helpers';

import { EventService, SeoService, ToastService } from '../../core/services';
import { AuthState } from '../../state';
import { ViewportScroller } from '@angular/common';

@Component({
  selector: 'gs-event-contributions',
  templateUrl: './event-contributions.component.html',
  standalone: false
})
export class EventContributionsComponent implements OnInit, OnDestroy {
  @Select(AuthState.isCorporateUser) isCorporateUser$: Observable<boolean>;

  user: UserProfile;
  contributions: any[];
  filteredContributions: any[];
  page: number = 1;
  itemsPerPage: number = 10;
  filter: string = "all";

  targetAmount: number = 0;
  totalContributionAmount: number = 0;
  averageContributionAmount: number = 0;
  settings: any;
  eventCode: string = '';
  event: any;

  hasCommentsEnabled: boolean = false;
  hasContributionsEnabled: boolean;

  get isEventOwner(): boolean {
    return this.event?.userId == this.user?.id;
  }

  private subscriptions: Subscription[] = [];

  constructor(
    private eventService: EventService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private viewportScroller: ViewportScroller,
    private seoService: SeoService,
  ) {

  }

  ngOnInit() {

    this.subscriptions.push(
      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          this.user = user;
        }),
      this.store.select<any>(state => state.staticData.publicSettings)
        .pipe(filter(x => !!x))
        .subscribe((data: any) => {
          this.hasCommentsEnabled = data.comments;
          this.hasContributionsEnabled = data.contribution;
        }),
    );

    this.eventCode = this.route.snapshot.paramMap.get('eventCode');
    this.getEvent();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getEvent() {
    this.subscriptions.push(
      this.eventService.getPublicEventByCode(this.eventCode)
        .pipe(
          catchError(e => {
            this.toastService.checkErrorForStatus(e);
            return throwError(e);
          }),
          finalize(() => { })
        )
        .subscribe((event: any) => {
          this.event = event;
          this.event.image = eventImage(event);
          this.event.userImage = userImage(event.userImage);
          this.event.recipientImage = userImage(event.recipientImage);
          this.targetAmount = event.targetAmount;
          this.totalContributionAmount = event.totalContributionAmount;
          this.averageContributionAmount = event.averageContributionAmount;
          this.contributions = event.contributions;

          if (event.hasContributionsHidden && !this.isEventOwner) {
            this.router.navigate(['/forbidden']);
            return;
          }

          // Custom SEO tags
          this.seoService.setTitle(`Contributii la ${this.event.name}`);
          this.seoService.setDescription(this.event.description);
          this.seoService.setImage(this.event.image);

          this.filterContributions();
        })
    );
  }

  onFilterChange() {
    this.resetPage();
    this.filterContributions();
    this.scroll();
  }

  onPageChanged() {
    this.scroll();
  }

  private filterContributions() {
    if (!this.contributions) {
      return;
    }

    switch (this.filter) {
      case 'last-week': {
        const referenceDate = addDays(new Date(), -7);
        this.filteredContributions = this.contributions.filter((x) => x.contributedDate >= referenceDate);
        break;
      }
      case 'last-month': {
        const referenceDate = addDays(new Date(), -30);
        this.filteredContributions = this.contributions.filter((x) => x.contributedDate >= referenceDate);
        break;
      }
      default: {
        this.filteredContributions = [...this.contributions];
        break;
      }
    }
  }

  private resetPage() {
    this.page = 1;
  }

  private scroll() {
    this.viewportScroller.scrollToPosition([0, 0]);
  }
}
